import { FC, useMemo } from 'react';
import FormGroup from '@mui/material/FormGroup';
import { useField } from 'informed';
import { AgreementsOutput } from '../../hooks/useAgreements';

import { FieldMap } from '../../model/FieldMap';

import { AgreementItemRecursive, LabelToggle } from '../index';
import { CommonProps } from '../../layout/LoanForm';
import { AgreementItem, CzerwonkaCheckbox } from './AgreementItemRecursive';

const AgreementsCheckboxesSection: FC<CommonProps & AgreementsOutput> = (props) => {
  const { t, agreements, requiredAgreementsIds } = props;

  const rootAgreements = useMemo(
    () => agreements.filter(({ parentId }) => !parentId),
    [agreements],
  );
  const childrenAgreementsParentIds = useMemo(
    () => agreements.filter(({ parentId }) => parentId).map(({ parentId }) => parentId),
    [agreements],
  );

  const uniqueChildrenAgreementsParentIds = useMemo(
    () => [...new Set(childrenAgreementsParentIds)],
    [childrenAgreementsParentIds],
  );
  const uniqueChildrenAgreements = useMemo(
    () =>
      uniqueChildrenAgreementsParentIds.reduce((acc, pId) => {
        return {
          ...acc,
          [`${pId}`]: agreements.filter(({ parentId }) => parentId === pId),
        };
      }, {}),
    [uniqueChildrenAgreementsParentIds, agreements],
  );

  const { fieldState, fieldApi, render } = useField({
    name: FieldMap.AGREEMENTS,
    validate: (agreementsChecked) => {
      if (
        (agreementsChecked as number[]).length > 0 &&
        !requiredAgreementsIds.every((id) => (agreementsChecked as number[]).includes(id))
      ) {
        return t.basicStep.checkRequired;
      }
    },
    validateOn: 'change',
    required: true,
  });

  // The field state ( contains value, error, dirty.. etc)
  const {
    value: agreementsChecked,
    dirty,
    error,
  } = fieldState as { value: number[]; [key: string]: any };

  // The field api ( allows you to control the field )
  const { setValue } = fieldApi;

  const allChecked = agreements.every(({ id }) => agreementsChecked.includes(id));

  const handleChange = (id: number) => {
    const removeItem = () => agreementsChecked.filter((_id) => id !== _id);

    agreementsChecked.includes(id) ? setValue(removeItem()) : setValue([...agreementsChecked, id]);
  };

  const handleToggleAll = (checked: boolean) => {
    checked ? setValue(agreements.map(({ id }) => id)) : setValue([]);
  };
  return (
    <FormGroup
      sx={(theme) => ({
        mt: '4rem',
        gap: '.7rem',

        [theme.breakpoints.up('sm')]: {
          gap: '3rem',
        },
      })}
    >
      <AgreementItem sx={{ mb: '2rem', bgcolor: '#F2F2F2' }}>
        <div className="agree-subrow">
          <CzerwonkaCheckbox
            id="selectAll"
            name="selectAll"
            checked={allChecked}
            indeterminate={!allChecked && agreementsChecked.length > 0}
            onChange={({ target }) => handleToggleAll(target.checked)}
          />
          <LabelToggle
            dirty={false}
            targetId="selectAll"
            t={t}
            content={t.basicStep.agreementAcceptAll}
            bold
          />
        </div>
      </AgreementItem>
      {render(
        rootAgreements.map((agreement, index) => {
          return (
            <AgreementItemRecursive
              t={t}
              key={index}
              agreement={agreement}
              agreementsChecked={agreementsChecked}
              requiredAgreementsIds={requiredAgreementsIds}
              uniqueChildrenAgreements={uniqueChildrenAgreements}
              handleChange={handleChange}
              dirty={dirty}
              error={error}
            />
          );
        }),
      )}
    </FormGroup>
  );
};

export default AgreementsCheckboxesSection;
